import { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  Loader2,
  Lock,
  CheckCircle2,
  XCircle,
  Calendar,
  Clock,
  User,
  Briefcase,
  MapPin,
  Award,
  FileText,
  ChevronLeft,
  ChevronRight,
  Phone,
  ExternalLink,
  AlertTriangle,
  Shield,
  Building2,
  GraduationCap,
  Star,
} from 'lucide-react';
import { format, parseISO, formatDistanceToNow, differenceInHours, differenceInMinutes, differenceInDays } from 'date-fns';
import { toast, Toaster } from 'sonner';

export default function PublicCVViewerPage() {
  const { token } = useParams();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [requiresPassword, setRequiresPassword] = useState(false);
  const [password, setPassword] = useState('');
  const [verifying, setVerifying] = useState(false);
  
  const [data, setData] = useState(null);
  const [selectedCandidate, setSelectedCandidate] = useState(0);
  
  // Decision dialog state
  const [decisionDialogOpen, setDecisionDialogOpen] = useState(false);
  const [pendingDecision, setPendingDecision] = useState(null); // 'approved' | 'rejected' | 'schedule_requested'
  const [decisionNotes, setDecisionNotes] = useState('');
  const [scheduleOption, setScheduleOption] = useState('call_me'); // 'pick_time' | 'call_me'
  const [preferredSlots, setPreferredSlots] = useState([{ date: '', time: '' }]);
  const [submitting, setSubmitting] = useState(false);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const loadData = useCallback(async (pwd = null) => {
    try {
      setLoading(true);
      setError(null);
      
      let url = `${API_URL}/api/public/cv-review/${token}`;
      if (pwd) {
        url += `?password=${encodeURIComponent(pwd)}`;
      }
      
      const response = await fetch(url);
      
      if (response.status === 410) {
        const err = await response.json();
        setError(err.detail || 'This link has expired or been revoked');
        return;
      }
      
      if (response.status === 401) {
        setError('Invalid password');
        return;
      }
      
      if (!response.ok) {
        const err = await response.json();
        setError(err.detail || 'Failed to load candidates');
        return;
      }
      
      const result = await response.json();
      
      if (result.requires_password) {
        setRequiresPassword(true);
        return;
      }
      
      setRequiresPassword(false);
      setData(result);
      
    } catch (err) {
      console.error('Load error:', err);
      setError('Failed to load. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [API_URL, token]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const verifyPassword = async () => {
    if (!password.trim()) {
      toast.error('Please enter the password');
      return;
    }
    
    setVerifying(true);
    try {
      const response = await fetch(`${API_URL}/api/public/cv-review/${token}/verify-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password })
      });
      
      const result = await response.json();
      
      if (result.valid) {
        // Load with password
        await loadData(password);
      } else {
        toast.error('Invalid password');
      }
    } catch (err) {
      toast.error('Failed to verify password');
    } finally {
      setVerifying(false);
    }
  };

  const openDecisionDialog = (decision) => {
    const candidate = data?.candidates?.[selectedCandidate];
    if (candidate?.decision_made) {
      toast.error('Decision already submitted for this candidate');
      return;
    }
    setPendingDecision(decision);
    setDecisionNotes('');
    setScheduleOption('call_me');
    setPreferredSlots([{ date: '', time: '' }]);
    setDecisionDialogOpen(true);
  };

  const submitDecision = async () => {
    const candidate = data?.candidates?.[selectedCandidate];
    if (!candidate) return;
    
    setSubmitting(true);
    try {
      const body = {
        candidate_id: candidate.candidate_id,
        decision: pendingDecision,
        notes: decisionNotes || null
      };
      
      // Add preferred slots for schedule requests
      if (pendingDecision === 'schedule_requested' && scheduleOption === 'pick_time') {
        const validSlots = preferredSlots.filter(s => s.date && s.time);
        if (validSlots.length > 0) {
          body.preferred_slots = validSlots;
        }
      }
      
      const response = await fetch(`${API_URL}/api/public/cv-review/${token}/decision`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      
      if (response.status === 429) {
        toast.error('Too many requests. Please wait a moment.');
        return;
      }
      
      if (!response.ok) {
        const err = await response.json();
        toast.error(err.detail || 'Failed to submit decision');
        return;
      }
      
      const result = await response.json();
      toast.success(`Decision recorded: ${pendingDecision === 'approved' ? 'Approved' : pendingDecision === 'rejected' ? 'Rejected' : 'Interview Requested'}`);
      
      setDecisionDialogOpen(false);
      
      // Refresh data to show updated decision status
      await loadData(password || null);
      
    } catch (err) {
      console.error('Decision error:', err);
      toast.error('Failed to submit decision');
    } finally {
      setSubmitting(false);
    }
  };

  const addSlot = () => {
    if (preferredSlots.length < 5) {
      setPreferredSlots([...preferredSlots, { date: '', time: '' }]);
    }
  };

  const updateSlot = (index, field, value) => {
    const newSlots = [...preferredSlots];
    newSlots[index][field] = value;
    setPreferredSlots(newSlots);
  };

  const removeSlot = (index) => {
    if (preferredSlots.length > 1) {
      setPreferredSlots(preferredSlots.filter((_, i) => i !== index));
    }
  };

  const formatExpiryCountdown = (expiresAt) => {
    if (!expiresAt) return '';
    try {
      const expiry = parseISO(expiresAt);
      const now = new Date();
      
      if (expiry < now) return 'Expired';
      
      const days = differenceInDays(expiry, now);
      const hours = differenceInHours(expiry, now) % 24;
      const minutes = differenceInMinutes(expiry, now) % 60;
      
      if (days > 0) return `${days}d ${hours}h remaining`;
      if (hours > 0) return `${hours}h ${minutes}m remaining`;
      return `${minutes}m remaining`;
    } catch {
      return '';
    }
  };

  // Password gate
  if (requiresPassword && !data) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
        <Toaster position="top-center" richColors />
        <Card className="w-full max-w-md bg-white/10 backdrop-blur-lg border-white/20">
          <CardHeader className="text-center">
            <div className="w-16 h-16 bg-indigo-600 rounded-full flex items-center justify-center mx-auto mb-4">
              <Lock className="w-8 h-8 text-white" />
            </div>
            <CardTitle className="text-white text-xl">Password Protected</CardTitle>
            <p className="text-slate-300 text-sm mt-2">
              Enter the password to view candidate profiles
            </p>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Input
                type="password"
                placeholder="Enter password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && verifyPassword()}
                className="bg-white/10 border-white/20 text-white placeholder:text-slate-400"
                data-testid="password-input"
              />
            </div>
            <Button 
              onClick={verifyPassword} 
              disabled={verifying}
              className="w-full bg-indigo-600 hover:bg-indigo-700"
              data-testid="verify-password-btn"
            >
              {verifying && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              View Candidates
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
        <Loader2 className="w-12 h-12 animate-spin text-indigo-500" />
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
        <Card className="w-full max-w-md bg-white/10 backdrop-blur-lg border-white/20 text-center">
          <CardContent className="pt-8 pb-8">
            <AlertTriangle className="w-16 h-16 text-amber-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-white mb-2">Unable to Load</h2>
            <p className="text-slate-300">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!data) return null;

  const currentCandidate = data.candidates?.[selectedCandidate];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      <Toaster position="top-center" richColors />
      
      {/* Header */}
      <header className="bg-slate-900/80 backdrop-blur-lg border-b border-white/10 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-lg flex items-center justify-center">
                <Building2 className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="text-lg font-semibold text-white" data-testid="cv-viewer-title">
                  {data.requirement_title}
                </h1>
                <p className="text-sm text-slate-400">
                  {data.client_safe_header} • Submitted by Daruwalla Consultancy
                </p>
              </div>
            </div>
            
            <div className="flex items-center gap-4">
              {data.expires_at && (
                <div className="hidden sm:flex items-center gap-2 text-sm text-slate-400">
                  <Clock className="w-4 h-4" />
                  <span>{formatExpiryCountdown(data.expires_at)}</span>
                </div>
              )}
              <Badge className="bg-indigo-600/20 text-indigo-300 border-indigo-500/30">
                {data.total_candidates} Candidate{data.total_candidates !== 1 ? 's' : ''}
              </Badge>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Candidate Navigation Tabs */}
        {data.candidates?.length > 1 && (
          <div className="mb-6">
            <div className="flex items-center gap-2 overflow-x-auto pb-2 scrollbar-hide">
              {data.candidates.map((candidate, idx) => (
                <button
                  key={candidate.candidate_id}
                  onClick={() => setSelectedCandidate(idx)}
                  className={`flex-shrink-0 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                    selectedCandidate === idx
                      ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-500/30'
                      : 'bg-white/10 text-slate-300 hover:bg-white/20'
                  }`}
                  data-testid={`candidate-tab-${idx}`}
                >
                  <span className="flex items-center gap-2">
                    {candidate.decision_made && (
                      <CheckCircle2 className="w-4 h-4 text-green-400" />
                    )}
                    {candidate.name || `Candidate ${idx + 1}`}
                  </span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Main Content */}
        {currentCandidate && (
          <div className="grid lg:grid-cols-2 gap-6">
            {/* Left: PDF Preview */}
            <Card className="bg-white/5 backdrop-blur-lg border-white/10 overflow-hidden">
              <CardHeader className="border-b border-white/10 pb-4">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-white flex items-center gap-2">
                      <FileText className="w-5 h-5 text-indigo-400" />
                      Resume Preview
                    </CardTitle>
                    {currentCandidate.resume_number && (
                      <p className="text-sm text-slate-400 mt-1 font-mono">
                        {currentCandidate.resume_number}
                      </p>
                    )}
                  </div>
                  {currentCandidate.generated_pdf_signed_url && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => window.open(currentCandidate.generated_pdf_signed_url, '_blank')}
                      className="bg-white/10 border-white/20 text-white hover:bg-white/20"
                    >
                      <ExternalLink className="w-4 h-4 mr-1" />
                      Open PDF
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent className="p-0">
                {currentCandidate.generated_pdf_signed_url ? (
                  <iframe
                    src={currentCandidate.generated_pdf_signed_url}
                    className="w-full h-[600px] bg-white"
                    title="Resume PDF"
                  />
                ) : (
                  <div className="h-[600px] flex items-center justify-center text-slate-400">
                    <FileText className="w-12 h-12 opacity-50" />
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Right: Candidate Info + Actions */}
            <div className="space-y-6">
              {/* Candidate Profile Card */}
              <Card className="bg-white/5 backdrop-blur-lg border-white/10">
                <CardContent className="pt-6">
                  <div className="flex items-start gap-4 mb-6">
                    <div className="w-16 h-16 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-full flex items-center justify-center flex-shrink-0">
                      <User className="w-8 h-8 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h2 className="text-xl font-bold text-white truncate">
                        {currentCandidate.parsed_summary?.full_name || 'Candidate'}
                      </h2>
                      {currentCandidate.parsed_summary?.current_title && (
                        <p className="text-indigo-300 flex items-center gap-2 mt-1">
                          <Briefcase className="w-4 h-4" />
                          {currentCandidate.parsed_summary.current_title}
                        </p>
                      )}
                      {currentCandidate.parsed_summary?.current_company && (
                        <p className="text-slate-400 text-sm">
                          at {currentCandidate.parsed_summary.current_company}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Key Stats */}
                  <div className="grid grid-cols-2 gap-4 mb-6">
                    {currentCandidate.parsed_summary?.location && (
                      <div className="flex items-center gap-2 text-slate-300">
                        <MapPin className="w-4 h-4 text-slate-500" />
                        <span className="text-sm">{currentCandidate.parsed_summary.location}</span>
                      </div>
                    )}
                    {currentCandidate.parsed_summary?.total_experience_years && (
                      <div className="flex items-center gap-2 text-slate-300">
                        <Award className="w-4 h-4 text-slate-500" />
                        <span className="text-sm">{currentCandidate.parsed_summary.total_experience_years} years exp.</span>
                      </div>
                    )}
                    {currentCandidate.parsed_summary?.experience_count > 0 && (
                      <div className="flex items-center gap-2 text-slate-300">
                        <Briefcase className="w-4 h-4 text-slate-500" />
                        <span className="text-sm">{currentCandidate.parsed_summary.experience_count} positions</span>
                      </div>
                    )}
                    {currentCandidate.parsed_summary?.education?.length > 0 && (
                      <div className="flex items-center gap-2 text-slate-300">
                        <GraduationCap className="w-4 h-4 text-slate-500" />
                        <span className="text-sm">{currentCandidate.parsed_summary.education[0]?.degree}</span>
                      </div>
                    )}
                  </div>

                  {/* Summary */}
                  {currentCandidate.parsed_summary?.summary && (
                    <div className="mb-6">
                      <h3 className="text-sm font-semibold text-slate-400 mb-2">Summary</h3>
                      <p className="text-slate-300 text-sm leading-relaxed">
                        {currentCandidate.parsed_summary.summary}
                      </p>
                    </div>
                  )}

                  {/* Skills */}
                  {currentCandidate.parsed_summary?.skills?.length > 0 && (
                    <div>
                      <h3 className="text-sm font-semibold text-slate-400 mb-2">Key Skills</h3>
                      <div className="flex flex-wrap gap-2">
                        {currentCandidate.parsed_summary.skills.map((skill, idx) => (
                          <Badge
                            key={idx}
                            className="bg-white/10 text-slate-300 border-white/20 text-xs"
                          >
                            {skill}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Decision Status / Actions */}
              {currentCandidate.decision_made ? (
                <Card className="bg-green-500/10 border-green-500/30">
                  <CardContent className="pt-6 text-center">
                    <CheckCircle2 className="w-12 h-12 text-green-400 mx-auto mb-3" />
                    <h3 className="text-lg font-semibold text-white mb-1">
                      Decision Submitted
                    </h3>
                    <p className="text-slate-300 text-sm">
                      You {currentCandidate.existing_decision?.decision === 'approved' ? 'approved' : 
                           currentCandidate.existing_decision?.decision === 'rejected' ? 'rejected' : 
                           'requested an interview for'} this candidate
                    </p>
                    <p className="text-slate-500 text-xs mt-2">
                      {currentCandidate.existing_decision?.decided_at && 
                        format(parseISO(currentCandidate.existing_decision.decided_at), 'PPp')}
                    </p>
                  </CardContent>
                </Card>
              ) : (
                <Card className="bg-white/5 backdrop-blur-lg border-white/10">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-white text-lg">Your Decision</CardTitle>
                    <p className="text-slate-400 text-sm">What would you like to do with this candidate?</p>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <Button
                      onClick={() => openDecisionDialog('approved')}
                      className="w-full bg-green-600 hover:bg-green-700 h-12 text-base"
                      data-testid="approve-btn"
                    >
                      <CheckCircle2 className="w-5 h-5 mr-2" />
                      Approve Candidate
                    </Button>
                    <Button
                      onClick={() => openDecisionDialog('schedule_requested')}
                      className="w-full bg-indigo-600 hover:bg-indigo-700 h-12 text-base"
                      data-testid="schedule-btn"
                    >
                      <Calendar className="w-5 h-5 mr-2" />
                      Request Interview
                    </Button>
                    <Button
                      onClick={() => openDecisionDialog('rejected')}
                      variant="outline"
                      className="w-full bg-white/5 border-white/20 text-slate-300 hover:bg-red-500/20 hover:text-red-300 hover:border-red-500/30 h-12 text-base"
                      data-testid="reject-btn"
                    >
                      <XCircle className="w-5 h-5 mr-2" />
                      Not a Fit
                    </Button>
                  </CardContent>
                </Card>
              )}

              {/* Navigation between candidates */}
              {data.candidates?.length > 1 && (
                <div className="flex items-center justify-between">
                  <Button
                    variant="outline"
                    onClick={() => setSelectedCandidate(Math.max(0, selectedCandidate - 1))}
                    disabled={selectedCandidate === 0}
                    className="bg-white/10 border-white/20 text-white hover:bg-white/20 disabled:opacity-50"
                  >
                    <ChevronLeft className="w-4 h-4 mr-1" />
                    Previous
                  </Button>
                  <span className="text-slate-400 text-sm">
                    {selectedCandidate + 1} of {data.candidates.length}
                  </span>
                  <Button
                    variant="outline"
                    onClick={() => setSelectedCandidate(Math.min(data.candidates.length - 1, selectedCandidate + 1))}
                    disabled={selectedCandidate === data.candidates.length - 1}
                    className="bg-white/10 border-white/20 text-white hover:bg-white/20 disabled:opacity-50"
                  >
                    Next
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
              )}
            </div>
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="border-t border-white/10 mt-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between text-sm text-slate-500">
            <p>Powered by Daruwalla Consultancy</p>
            <p className="flex items-center gap-1">
              <Shield className="w-4 h-4" />
              Secure & Confidential
            </p>
          </div>
        </div>
      </footer>

      {/* Decision Dialog */}
      <Dialog open={decisionDialogOpen} onOpenChange={setDecisionDialogOpen}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {pendingDecision === 'approved' && <CheckCircle2 className="w-5 h-5 text-green-400" />}
              {pendingDecision === 'rejected' && <XCircle className="w-5 h-5 text-red-400" />}
              {pendingDecision === 'schedule_requested' && <Calendar className="w-5 h-5 text-indigo-400" />}
              {pendingDecision === 'approved' && 'Approve Candidate'}
              {pendingDecision === 'rejected' && 'Reject Candidate'}
              {pendingDecision === 'schedule_requested' && 'Request Interview'}
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              {pendingDecision === 'approved' && 'Confirm your approval of this candidate.'}
              {pendingDecision === 'rejected' && 'Let us know why this candidate is not a fit.'}
              {pendingDecision === 'schedule_requested' && 'How would you like to schedule the interview?'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Schedule Options (only for schedule_requested) */}
            {pendingDecision === 'schedule_requested' && (
              <div className="space-y-3">
                <Label className="text-slate-300">Schedule Option</Label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={() => setScheduleOption('call_me')}
                    className={`p-3 rounded-lg border text-left transition-all ${
                      scheduleOption === 'call_me'
                        ? 'bg-indigo-600/20 border-indigo-500 text-white'
                        : 'bg-white/5 border-white/20 text-slate-300 hover:bg-white/10'
                    }`}
                  >
                    <Phone className="w-5 h-5 mb-2" />
                    <p className="font-medium text-sm">Call me to schedule</p>
                    <p className="text-xs text-slate-400">Daruwalla will call you</p>
                  </button>
                  <button
                    onClick={() => setScheduleOption('pick_time')}
                    className={`p-3 rounded-lg border text-left transition-all ${
                      scheduleOption === 'pick_time'
                        ? 'bg-indigo-600/20 border-indigo-500 text-white'
                        : 'bg-white/5 border-white/20 text-slate-300 hover:bg-white/10'
                    }`}
                  >
                    <Calendar className="w-5 h-5 mb-2" />
                    <p className="font-medium text-sm">I'll pick a time</p>
                    <p className="text-xs text-slate-400">Select preferred slots</p>
                  </button>
                </div>

                {scheduleOption === 'pick_time' && (
                  <div className="space-y-3 pt-2">
                    <Label className="text-slate-300">Preferred Time Slots</Label>
                    {preferredSlots.map((slot, idx) => (
                      <div key={idx} className="flex gap-2">
                        <Input
                          type="date"
                          value={slot.date}
                          onChange={(e) => updateSlot(idx, 'date', e.target.value)}
                          className="bg-white/10 border-white/20 text-white flex-1"
                        />
                        <Input
                          type="time"
                          value={slot.time}
                          onChange={(e) => updateSlot(idx, 'time', e.target.value)}
                          className="bg-white/10 border-white/20 text-white w-32"
                        />
                        {preferredSlots.length > 1 && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => removeSlot(idx)}
                            className="text-slate-400 hover:text-red-400"
                          >
                            <XCircle className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    ))}
                    {preferredSlots.length < 5 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={addSlot}
                        className="text-indigo-400 hover:text-indigo-300"
                      >
                        + Add another slot
                      </Button>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Notes */}
            <div>
              <Label className="text-slate-300">
                {pendingDecision === 'rejected' ? 'Reason (optional)' : 'Notes (optional)'}
              </Label>
              <Textarea
                value={decisionNotes}
                onChange={(e) => setDecisionNotes(e.target.value)}
                placeholder={
                  pendingDecision === 'rejected'
                    ? 'Why is this candidate not a fit?'
                    : 'Any additional notes...'
                }
                className="bg-white/10 border-white/20 text-white placeholder:text-slate-500 mt-2"
                rows={3}
              />
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setDecisionDialogOpen(false)}
              className="bg-white/5 border-white/20 text-slate-300 hover:bg-white/10"
            >
              Cancel
            </Button>
            <Button
              onClick={submitDecision}
              disabled={submitting}
              className={
                pendingDecision === 'approved' ? 'bg-green-600 hover:bg-green-700' :
                pendingDecision === 'rejected' ? 'bg-red-600 hover:bg-red-700' :
                'bg-indigo-600 hover:bg-indigo-700'
              }
              data-testid="confirm-decision-btn"
            >
              {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Confirm
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
